import dayjs from "dayjs"
import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { getLabel } from "./getNameFromEmail.js";
import { getUserPickervalues } from "./requestHelper.js";
import { safeDayjs } from "./safeDate.js";
import { compareNumbers } from "./numberFormatter.js";
import { resolveFieldTranslation } from "./formTranslations.js";

// Kept as a re-export: safeDayjs lives in ./safeDate.js now, but plenty of
// modules already import it from here.
export { safeDayjs };

export const getSectionChildId = (droppableId) => {
  let droppableIdSplits = droppableId.split('section-child-');
  let sectionElementId = droppableIdSplits[1]
  let sectionChildIds = sectionElementId.split('item-parent-')
  let sectionId = sectionChildIds[1]
  return { sectionId: sectionId, elementId: sectionChildIds[0] }
}
export const recursiveInsert = (items, sectionId, newItem, destinationIndex) => {
  return items.map(item => {
    // Ensure strict string comparison to avoid type mismatchessole.
    if (String(item.id).trim() === String(sectionId).trim()) {

      let newChildren = item.children ? [...item.children] : [];
      // Defensive check for bounds
      if (destinationIndex < 0) destinationIndex = 0;
      if (destinationIndex > newChildren.length) destinationIndex = newChildren.length;

      newChildren.splice(destinationIndex, 0, newItem);
      return { ...item, children: newChildren };
    }

    if (item.children && Array.isArray(item.children)) {
      return { ...item, children: recursiveInsert(item.children, sectionId, newItem, destinationIndex) };
    }
    return item;
  });
}

export const updateElementWidth = (items, id) => {
  return items.map(item => {
    if (item.id === id) {
      return { ...item, span: item.span === "24" ? "12" : "24" }
    } else if (item.children) {
      return { ...item, children: updateElementWidth(item.children, id) }
    }
    return item
  })
}

export const recursiveDelete = (items, id) => {
  return items
    .filter((item) => item.id !== id)
    .map((item) => {
      if (!item.children || !Array.isArray(item.children)) return item;
      item.children = recursiveDelete(item.children, id);
      return item;
    });
};

export const findElementById = (items, id) => {
  for (const item of items) {
    if (item.id === id) return item;
    if (item.children && item.children.length > 0) {
      const found = findElementById(item.children, id);
      if (found) return found;
    }
  }
  return null;
};

export const findParentWithChild = (items, childId) => {
  for (const item of items) {
    if (item.children && Array.isArray(item.children)) {
      if (item.children.some((child) => child.id === childId)) {
        return item;
      }
      const found = findParentWithChild(item.children, childId);
      if (found) return found;
    }
  }
  return null;
};

export const recursiveUpdate = (
  array,
  id,
  updatedValues
) => {
  return array.map((item) => {
    if (item.id === id) {
      // Update the item with new values
      return { ...item, ...updatedValues };
    } else if (item.children) {
      // Recursively update children if present
      return {
        ...item,
        children: recursiveUpdate(item.children, id, { ...updatedValues }),
      };
    }
    return item;
  });
};

export const getAllFields = (elements, formattedFields) => {
  elements.forEach(element => {
    formattedFields.push({ label: element.label, value: element.id })
    if (element.children) {
      getAllFields(element.children, formattedFields)
    }
  })
  return formattedFields
}

// ── Per-field form-value dependencies ────────────────────────────────────────
// A field re-renders only when a value it can actually read changes. These two
// exports say which values those are, so callers can hand each field a stable
// slice instead of the whole store (whose identity changes on every keystroke).

// Types that read values of fields they cannot be statically linked to - a
// formula's operands, a user picker's source dropdown, a table's rows merged with
// the whole value set, a section's children. They get the real object.
export const READS_ALL_FORM_VALUES = new Set(["userForm", "tableForm", "numberForm", "section"])

// Every other widget reads `formValues[ownId]`, plus whatever its own config and the
// conditions targeting it name. A table reference is looked up three ways by
// formConditionsHandler (the composite id, the bare column id, and the table's rows),
// so all three are dependencies.
// Cached per (field object, conditions array) identity. Without this the loop below runs
// for every field on every keystroke - O(fields x conditions x rows) of pure overhead on
// a request form that has conditions.
const EMPTY_CONDITIONS = []
const fieldDepsCache = new WeakMap()

export const fieldValueDeps = (data, conditions) => {
  let perField = fieldDepsCache.get(data)
  if (perField) {
    const hit = perField.get(conditions || EMPTY_CONDITIONS)
    if (hit) return hit
  } else {
    perField = new WeakMap()
    fieldDepsCache.set(data, perField)
  }

  const keys = new Set([data.id])
  const addRef = (ref) => {
    if (!ref) return
    keys.add(ref)
    if (String(ref).includes("::")) {
      const [tableId, columnId] = String(ref).split("::")
      keys.add(tableId)
      keys.add(columnId)
    }
  }
  if (data.formType === "dateForm") {
    addRef(data.relativeDateReference)
    addRef(data.relativeDateCountField)
  }
  for (const condition of conditions || []) {
    if (!(condition.actions || []).some(action => action.field === data.id)) continue
    for (const scenario of condition.scenarios || []) {
      for (const row of scenario.conditions || []) addRef(row.reference)
    }
    for (const action of condition.actions || []) {
      if (action.field === data.id && action.valueSource === "dynamic") addRef(action.dynamicField)
    }
  }
  const result = Array.from(keys)
  perField.set(conditions || EMPTY_CONDITIONS, result)
  return result
}

// Union of every value key the fields inside a section (at any depth) can read.
// Lets a section skip its entire subtree - antd Collapse included - when a keystroke
// touched nothing it contains. Cached on the children array's identity.
const sectionDepsCache = new WeakMap()
export const sectionValueDeps = (children, conditions) => {
  if (!Array.isArray(children)) return []
  const cached = sectionDepsCache.get(children)
  if (cached && cached.conditions === conditions) return cached.keys
  const keys = new Set()
  const walk = (list) => {
    for (const item of list || []) {
      if (!item) continue
      if (item.formType === "section") {
        keys.add(item.id)
        walk(item.children)
      } else {
        for (const k of fieldValueDeps(item, conditions)) keys.add(k)
      }
    }
  }
  walk(children)
  const result = Array.from(keys)
  sectionDepsCache.set(children, { conditions, keys: result })
  return result
}

// Builds the per-field slice described above, reusing the previous slice object while
// the values it exposes are unchanged - that stable identity is what lets React.memo
// skip a field. One slicer per rendering container (canvas, section, request form);
// keep it in a ref so the cache survives re-renders.
export const createFormValuesSlicer = () => {
  const cache = new Map()
  return (data, formValues, conditions) => {
    // Every field gets a slice - never undefined. Wrapper turns undefined into {} and {}
    // is truthy, so returning undefined made the condition engine evaluate a field
    // against an empty value set: "is equal to" never fired and "is empty" always did.
    // A section needs the whole set: it slices for its own children.
    if (READS_ALL_FORM_VALUES.has(data.formType)) return formValues
    const keys = fieldValueDeps(data, conditions)
    const values = keys.map(k => formValues?.[k])
    // Presence matters as well as value: formConditionsHandler distinguishes a
    // row-scoped table lookup from a top-level one with hasOwnProperty, and Wrapper
    // spreads this slice OVER the saved request values.
    const has = keys.map(k => !!formValues && Object.prototype.hasOwnProperty.call(formValues, k))
    const cached = cache.get(data.id)
    if (cached && cached.keys.length === keys.length &&
        cached.keys.every((k, i) => k === keys[i]) &&
        cached.values.every((v, i) => v === values[i]) &&
        cached.has.every((h, i) => h === has[i])) {
      return cached.slice
    }
    const slice = {}
    keys.forEach((k, i) => { if (has[i]) slice[k] = values[i] })
    cache.set(data.id, { keys, values, has, slice })
    return slice
  }
}

// Cached on the array's identity. Element arrays are never mutated in place
// (recursiveUpdate maps, recursiveDelete filters, Index.jsx copies before pushing),
// so a cache hit is always current. Only the top-level call is cached - the
// sectionLabel recursion produces a different shape for the same children array.
// The returned array is SHARED: treat it as read-only (filter/map/find are fine,
// push/sort/splice are not).
const widgetCache = new WeakMap()

export const getAllWidgets = (list, sectionLabel = '', sectionPath = []) => {
  let mod = [], itemList = []
  if (!list) {
    return mod
  }
  if (!sectionLabel && widgetCache.has(list)) {
    return widgetCache.get(list)
  }
  itemList = list.filter(item => item)
  itemList.forEach(listItem => {
    if (listItem.formType !== "section") {
      // && listItem.formType !== "infoForm") {
      let reqObj = { ...listItem }
      if (sectionLabel) {
        reqObj.sectionLabel = sectionLabel
      }
      if (sectionPath.length) {
        reqObj.sectionPath = sectionPath
      }
      mod.push(reqObj)

      if (listItem.formType === "tableForm" && listItem.columns) {
        listItem.columns.forEach(col => {
          let formType = "inputForm";
          if (col.inputType === "number") formType = "numberForm";
          else if (col.inputType === "date") formType = "dateForm";
          else if (col.inputType === "time") formType = "timeForm";
          else if (col.inputType === "select" || col.inputType === "dropdown") formType = "dropdownField";
          else if (col.inputType === "textAreaForm") formType = "textAreaForm";
          else if (col.inputType === "checkbox") formType = "checkboxField";
          else if (col.inputType === "radio") formType = "radioField";
          else if (col.inputType === "link") formType = "linkField";
          else if (col.inputType === "mcq") formType = "multipleChoiceField";

          mod.push({
            ...col,
            id: listItem.id + "::" + col.id,
            label: `${col.label} (${listItem.label || listItem.formLabel || "Table Column"})`,
            actualLabel: col.label,
            formType: formType,
            sectionLabel: listItem.label,
            sectionPath: sectionPath.length ? sectionPath : undefined,
            parentTableId: listItem.id,
            formLabel: listItem.formLabel || ''
          })
        })
      }
    } else if (listItem.formType === "section") {
      const nextPath = [...sectionPath, { id: listItem.id, label: listItem.label }]
      mod.push(listItem)
      mod.push(...getAllWidgets(listItem.children || [], listItem.label, nextPath))
    }
  })
  if (!sectionLabel) {
    widgetCache.set(list, mod)
  }
  return mod
}

// First-wins map of widget id -> widget. Several item trees can be passed (the
// current form, then every form in the workflow) so a dynamic "set" can resolve
// a source field that lives on a previous step's form.
export const getWidgetsById = (...elementLists) => {
  const map = new Map()
  for (const list of elementLists) {
    if (!Array.isArray(list) || list.length === 0) continue
    getAllWidgets(list).forEach((widget) => {
      if (widget?.id != null && !map.has(widget.id)) {
        map.set(widget.id, widget)
      }
    })
  }
  return map
}

export const flattenFormItems = (forms = []) => {
  if (!Array.isArray(forms)) return []
  return forms.flatMap((form) => form?.items || [])
}

// Keys stored on already-submitted steps. Freeze follows that snapshot: visible
// fields and hidden fields that a "set" wrote, not every widget on the form.
export const getPreviousStepFrozenFields = (workflow = []) => {
  const frozen = {}
  if (!Array.isArray(workflow) || workflow.length <= 1) {
    return frozen
  }
  workflow.slice(0, -1).forEach((step) => {
    if (step?.formValues && typeof step.formValues === "object") {
      Object.keys(step.formValues).forEach((key) => {
        frozen[key] = true
      })
    }
  })
  return frozen
}

// Outermost (top-level) sections only that have scoring/questionnaire enabled on themselves.
export const getQuestionnaireSections = (list) => {
  return (list || [])
    .filter(item => item && item.formType === "section" && item.isQuestionnaireSection)
    .map(item => ({ id: item.id, label: item.label }))
}

// Sections whose immediate parent section has isQuestionnaireSection enabled.
export const getQuestionnaireSubSections = (list, parentIsQuestionnaire = false) => {
  let result = []
  ;(list || []).filter(item => item).forEach(item => {
    if (item.formType === "section") {
      if (parentIsQuestionnaire) {
        result.push({ id: item.id, label: item.label })
      }
      result.push(...getQuestionnaireSubSections(item.children || [], !!item.isQuestionnaireSection))
    }
  })
  return result
}

// Questions (option-based fields) that have enableScoring turned on.
export const getScoringQuestions = (list) => {
  return getAllWidgets(list).filter(field => field.enableScoring)
}

// Flattens the assessmentScore.fieldWiseScore tree (sections/sub-sections nested
// via `children`, questions identified by `questionId`) into a lookup keyed by
// the section/question id, so a Score condition can look up a score by reference.
export const flattenScoreTree = (fieldWiseScore) => {
  let lookup = {}
  const walk = (nodes) => {
    (nodes || []).filter(node => node).forEach(node => {
      if (Array.isArray(node.children)) {
        if (node.sectionId) {
          lookup[node.sectionId] = { scoreObtained: node.scoreObtained, totalSectionScore: node.totalSectionScore }
        }
        walk(node.children)
      } else if (node.questionId) {
        lookup[node.questionId] = { scoreObtained: node.scoreObtained }
      }
    })
  }
  walk(fieldWiseScore)
  return lookup
}

// True when a condition reads a Score anywhere in its scenarios. Highlights split on
// this: a score-referencing condition paints the assessment score panel, every other
// condition paints the live form, so the two never highlight the same thing twice.
export const isScoreBasedCondition = (condition) => {
  return (condition?.scenarios || []).some(scenario =>
    (scenario?.conditions || []).some(item => item?.parent === "Score")
  )
}

// The colour a "highlight" condition action paints its target with, or null when
// no highlight applies. Works for any target - a section or a single field - and
// for any scenario type, so it is shared by the form renderer and the score panel.
export const getHighlightAction = (actions) => {
  const highlightAction = (actions || []).find(
    action => action && typeof action !== "string" && action.action === "highlight" && action.value
  )
  return highlightAction || null
}

// Readable (black/white) text colour for an arbitrary background, so a highlighted
// target stays legible no matter which colour was picked on the condition.
export const getContrastTextColor = (hex) => {
  let parsed = (hex || "").replace('#', '')
  if (parsed.length === 3) {
    parsed = parsed.split('').map(ch => ch + ch).join('')
  }
  const r = parseInt(parsed.substring(0, 2), 16)
  const g = parseInt(parsed.substring(2, 4), 16)
  const b = parseInt(parsed.substring(4, 6), 16)
  if ([r, g, b].some(Number.isNaN)) return "#1f1f1f"
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return luminance > 0.6 ? "#1f1f1f" : "#ffffff"
}

// Emptiness for condition purposes: no value at all, a blank string, an empty
// list (multi-select, attachments, table column values) or an object that has
// been cleared back to {}. Booleans and 0 are values, not blanks.
export const isValueEmpty = (value) => {
  if (value === undefined || value === null || value === "") return true
  if (Array.isArray(value)) return value.length === 0
  if (typeof value === "object") return Object.keys(value).length === 0
  return false
}

// Profile-mapped fields may fill from the signed-in user only while that user
// is authoring their own new/draft form. Another viewer (or a submitted request)
// must keep whatever was saved — including a blank when the requester was not
// in master data.
export const shouldSkipProfileAutofill = ({ requestDetail, defaultRequestDetail, userEmail } = {}) => {
  const persisted = requestDetail?.requestId
    ? requestDetail
    : (defaultRequestDetail?.requestId ? defaultRequestDetail : null)
  if (!persisted) return false

  const creatorEmail = persisted?.createdBy?.email
  if (creatorEmail && userEmail && String(creatorEmail).toLowerCase() !== String(userEmail).toLowerCase()) {
    return true
  }

  const status = persisted?.status
  return !!(status && status !== "draft")
}

// A date field with "today" switched on fills itself in with the current date. That
// default belongs to filling the form in, not to reading it back: once the request is
// submitted, the date it was submitted with is part of the record, so opening it again
// - or an approver opening it on a later step - must not roll it forward to their own
// today. A request that was never saved and one still in draft are both being filled
// in, so they keep the default; so does a field a submitted request holds no value for
// yet, which is a later step's form being filled in for the first time.
export const shouldApplyTodayDefault = ({ requestDetail, defaultRequestDetail, fieldId, viewOnly = false } = {}) => {
  // A form nobody can fill in is being read back, not filled in - a previously
  // submitted public form, or a cycle/step that is already closed. Those carry no
  // request detail of their own, so this is the only thing that marks them.
  if (viewOnly) return false

  const persisted = requestDetail?.requestId
    ? requestDetail
    : (defaultRequestDetail?.requestId ? defaultRequestDetail : null)
  if (!persisted) return true

  const status = persisted.status
  if (!status || status === "draft") return true

  const storedValue = defaultRequestDetail?.value?.[fieldId] ?? persisted?.value?.[fieldId]
  return storedValue === undefined || storedValue === null || storedValue === ""
}

// Comparators that let the author pick several options at once. "does not contain"
// only qualifies on a multiple choice reference, where the answer itself is a list.
export const isMultiSelectComparator = (comparator, formType) => {
  if (comparator === "containsAnyOf" || comparator === "containsAllOf") return true
  return comparator === "doesNotcontain" && formType === "multipleChoiceField"
}

// True when a stored condition value already holds several picks. The generic
// comparison paths can't see the reference's form type, so they read the shape.
export const isConditionValueList = (value) => {
  if (Array.isArray(value)) return true
  if (typeof value !== "string" || !value.trim().startsWith("[")) return false
  try {
    return Array.isArray(JSON.parse(value))
  } catch (error) {
    return false
  }
}

// Multi option comparators store their picks as a JSON array string, but conditions
// authored before that stored a single option, so both shapes are read back here.
export const parseConditionValueList = (value) => {
  if (Array.isArray(value)) return value
  if (value === undefined || value === null || value === "") return []
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value)
      return Array.isArray(parsed) ? parsed : [value]
    } catch (error) {
      return [value]
    }
  }
  return [value]
}

// Dropdown / multi-choice answers are a single value or a list; condition picks
// are stored as a JSON array string. Loose `==` covers number/string option ids.
const unwrapOptionValue = (value) => {
  if (value && typeof value === "object" && !Array.isArray(value) && (value.value !== undefined || value.label !== undefined)) {
    return [value.value, value.label].filter((part) => part !== undefined && part !== null && part !== "")
  }
  return value === undefined || value === null || value === "" ? [] : [value]
}

const asSelectedValues = (currentValue) => {
  if (currentValue === undefined || currentValue === null || currentValue === "") return []
  if (typeof currentValue === "string") return [currentValue]
  if (Array.isArray(currentValue)) return currentValue.flatMap(unwrapOptionValue)
  const fromObject = unwrapOptionValue(currentValue)
  if (fromObject.length) return fromObject
  return parseConditionValueList(currentValue)
}

const scalarEquals = (left, right) => {
  if (left == right) return true
  if (left === undefined || left === null || right === undefined || right === null) return false
  return String(left).trim() === String(right).trim()
}

const optionAliases = (widget, value) => {
  const aliases = [value]
  const options = Array.isArray(widget?.options) ? widget.options : []
  options.forEach((option) => {
    const optionValue = option?.value ?? option
    const optionLabel = typeof option === "object" ? option.label : option
    // `rowUuids` are the master rows an option stands for. A master column that
    // points at another table is keyed by the row it points at, so an answer or a
    // condition stored under the row it was read from means the same choice.
    const rowUuids = (typeof option === "object" && Array.isArray(option.rowUuids)) ? option.rowUuids : []
    if (scalarEquals(optionValue, value) || scalarEquals(optionLabel, value)
      || rowUuids.some((uuid) => scalarEquals(uuid, value))) {
      aliases.push(optionValue, optionLabel, ...rowUuids)
    }
  })
  return aliases
}

const selectionMatchesWanted = (selected, wanted, widget) => {
  const wantedAliases = optionAliases(widget, wanted)
  const selectedAliases = optionAliases(widget, selected)
  return wantedAliases.some((wantedAlias) => selectedAliases.some((selectedAlias) => scalarEquals(selectedAlias, wantedAlias)))
}

// Equals for dropdown / text / unknown types. The public form may not resolve a
// widget's formType, and a master option can be stored as an id or a label.
export const valuesAreEqual = (currentValue, conditionValue, widget) => {
  if (currentValue === undefined || currentValue === null || currentValue === "") return false
  const selectedValues = asSelectedValues(currentValue)
  const conditionValues = parseConditionValueList(conditionValue)
  if (!selectedValues.length || !conditionValues.length) return false
  return conditionValues.some((wanted) => selectedValues.some((selected) => selectionMatchesWanted(selected, wanted, widget)))
}

export const valuesContainAnyOf = (currentValue, conditionValue, widget) => {
  return valuesAreEqual(currentValue, conditionValue, widget)
}

export const valuesContainAllOf = (currentValue, conditionValue, widget) => {
  const selectedValues = asSelectedValues(currentValue)
  const conditionValues = parseConditionValueList(conditionValue)
  if (!selectedValues.length || !conditionValues.length) return false
  return conditionValues.every((wanted) => selectedValues.some((selected) => selectionMatchesWanted(selected, wanted, widget)))
}

export const userFormContainsAnyOf = (currentValue, conditionValue) => {
  if (!currentValue || !conditionValue) return false
  if (conditionValue?.user?.length > 0 && currentValue?.user?.length > 0) {
    if (currentValue.user.some((user) => conditionValue.user.includes(user))) return true
  }
  if (conditionValue?.group?.length > 0 && currentValue?.group?.length > 0) {
    if (currentValue.group.some((group) => conditionValue.group.includes(group))) return true
  }
  return false
}

export const TEXT_SET_FORM_TYPES = ["inputForm", "textAreaForm"]
export const NUMBER_SET_FORM_TYPES = ["numberForm"]
export const OPTION_SET_FORM_TYPES = ["dropdownField", "radioField", "multipleChoiceField"]
export const USER_SET_FORM_TYPES = ["userForm"]

export const isEmailTextField = (element) =>
  !!element?.isEmail && TEXT_SET_FORM_TYPES.includes(element?.formType)

// Optional sign, digits, optional fraction. Leading zeros count ("01").
const NUMERIC_LABEL_PATTERN = /^[+-]?(?:\d+(?:\.\d*)?|\.\d+)$/

export const isValidNumericLabel = (label) => {
  if (label === undefined || label === null) return false
  const text = String(label).trim()
  if (!text) return false
  return NUMERIC_LABEL_PATTERN.test(text) && Number.isFinite(Number(text))
}

// Dynamic "set" copies another field's current answer. Same-type always works.
// Text and number targets also accept option fields (dropdown / radio /
// multiple choice). Dropdown and radio can copy from each other. A user picker
// can be filled from another user picker or from an email text field, and an
// email text field can be filled from a user picker.
export const isCompatibleDynamicSetSource = (sourceElement, targetElement) => {
  const sourceType = typeof sourceElement === "string" ? sourceElement : sourceElement?.formType
  const targetType = typeof targetElement === "string" ? targetElement : targetElement?.formType
  const source = typeof sourceElement === "object" ? sourceElement : null
  const target = typeof targetElement === "object" ? targetElement : null
  if (!sourceType || !targetType) return false
  if (sourceType === targetType) return true
  if (["dropdownField", "radioField"].includes(targetType) && ["dropdownField", "radioField"].includes(sourceType)) {
    return true
  }
  if (TEXT_SET_FORM_TYPES.includes(targetType) && (
    TEXT_SET_FORM_TYPES.includes(sourceType) || OPTION_SET_FORM_TYPES.includes(sourceType)
  )) {
    return true
  }
  if (NUMBER_SET_FORM_TYPES.includes(targetType) && (
    NUMBER_SET_FORM_TYPES.includes(sourceType) || OPTION_SET_FORM_TYPES.includes(sourceType)
  )) {
    return true
  }
  if (USER_SET_FORM_TYPES.includes(targetType) && isEmailTextField(source)) {
    return true
  }
  if (isEmailTextField(target) && USER_SET_FORM_TYPES.includes(sourceType)) {
    return true
  }
  return false
}

const optionLabelFromElement = (sourceElement, value, lng) => {
  const localizedElement = resolveFieldTranslation(sourceElement, lng)
  const options = Array.isArray(localizedElement?.options) ? localizedElement.options : []
  const option = options.find((opt) => String(opt?.value ?? opt) === String(value))
  if (!option) return value
  return typeof option === "object" ? (option.label ?? option.value) : option
}

const resolveOptionLabels = (rawValue, sourceElement, getValueLabel, lng) => {
  const selectedValues = parseConditionValueList(rawValue)
  return selectedValues.map((value) => {
    const resolved = typeof getValueLabel === "function"
      ? getValueLabel(sourceElement, value, lng)
      : optionLabelFromElement(sourceElement, value, lng)
    if (resolved === undefined || resolved === null || resolved === "") {
      return value
    }
    return resolved
  })
}

const USER_EMAIL_SPLIT_PATTERN = /[,;\n]+/

const optionValueKeys = (option) => {
  if (!option || typeof option !== "object") {
    return option === undefined || option === null ? [] : [String(option)]
  }
  const keys = option.value === undefined || option.value === null ? [] : [String(option.value)]
  if (Array.isArray(option.rowUuids)) {
    option.rowUuids.forEach((uuid) => { if (uuid !== undefined && uuid !== null) keys.push(String(uuid)) })
  }
  return keys
}

// A dropdown / radio / multiple choice set from another one copies the stored id,
// which only lands when both fields are keyed the same way. Two fields reading the
// same choice off different masters - a user role's Business Unit column feeding the
// Business Unit dropdown - need not be, and the target would then hold an id it has
// no option for and show it raw. The display text is what the two have in common, so
// an id the target does not know is matched on its label; one it does know is left
// alone, and so is one that matches nothing.
const matchTargetOptionValue = (rawValue, sourceElement, targetElement, getValueLabel, lng) => {
  const targetOptions = Array.isArray(targetElement?.options) ? targetElement.options : []
  if (targetOptions.length === 0) {
    return rawValue
  }
  const readLabel = (element, value) => {
    const label = typeof getValueLabel === "function"
      ? getValueLabel(element, value, lng)
      : optionLabelFromElement(element, value, lng)
    return label === undefined || label === null ? "" : String(label).trim()
  }
  const knownValues = new Set()
  const byLabel = new Map()
  const byLooseLabel = new Map()
  targetOptions.forEach((option) => {
    optionValueKeys(option).forEach((key) => knownValues.add(key))
    const value = option && typeof option === "object" ? option.value : option
    if (value === undefined || value === null || value === "") return
    const label = readLabel(targetElement, value)
    if (!label) return
    if (!byLabel.has(label)) byLabel.set(label, value)
    const loose = label.toLowerCase()
    if (!byLooseLabel.has(loose)) byLooseLabel.set(loose, value)
  })

  const selected = parseConditionValueList(rawValue)
  if (selected.length === 0) {
    return rawValue
  }
  const matched = selected.map((value) => {
    if (value === undefined || value === null || value === "" || knownValues.has(String(value))) {
      return value
    }
    const label = readLabel(sourceElement, value)
    if (!label) return value
    return byLabel.get(label) ?? byLooseLabel.get(label.toLowerCase()) ?? value
  })
  if (Array.isArray(rawValue)) {
    return matched
  }
  return matched.length === 1 ? matched[0] : matched
}

const emailsFromRawSetValue = (rawValue) => {
  if (rawValue === undefined || rawValue === null || rawValue === "") return []
  if (Array.isArray(rawValue)) {
    return rawValue.flatMap(emailsFromRawSetValue)
  }
  if (typeof rawValue === "object") {
    return Array.isArray(rawValue.user) ? rawValue.user.filter(Boolean) : []
  }
  return String(rawValue)
    .split(USER_EMAIL_SPLIT_PATTERN)
    .map((part) => part.trim())
    .filter(Boolean)
}

const toUserPickerSetValue = (emails) => {
  const unique = Array.from(new Set(emails.filter(Boolean).map((email) => String(email).trim()).filter(Boolean)))
  if (unique.length === 0) return null
  return { user: unique, group: [], role: [] }
}

// When a text field is set from a dropdown / radio / multiple choice, store the
// option label(s) rather than the stored option id. Several picks become a
// comma-separated list. Labels are resolved in `lng` so the text matches the
// option the reader currently sees. A number field only writes when every
// selected label is a valid number ("01" included); otherwise nothing is copied.
// A user picker copies another picker as-is, or puts an email text field's
// value on `user`. An email text field copies the first selected user email.
export const resolveDynamicSetValue = (rawValue, sourceElement, targetFormType, getValueLabel, lng, targetElement) => {
  if (rawValue === undefined || rawValue === null || rawValue === "") {
    return rawValue
  }

  if (USER_SET_FORM_TYPES.includes(targetFormType)) {
    if (sourceElement?.formType === "userForm") {
      return rawValue
    }
    const emails = emailsFromRawSetValue(rawValue)
    const withAt = emails.filter((email) => String(email).includes("@"))
    return toUserPickerSetValue(withAt.length > 0 ? withAt : emails)
  }

  if (TEXT_SET_FORM_TYPES.includes(targetFormType) && sourceElement?.formType === "userForm") {
    const emails = emailsFromRawSetValue(rawValue)
    if (emails.length === 0) return null
    // Email text fields validate a single address.
    if (targetElement?.isEmail) return emails[0]
    return emails.join(", ")
  }

  if (!OPTION_SET_FORM_TYPES.includes(sourceElement?.formType)) {
    return rawValue
  }

  if (TEXT_SET_FORM_TYPES.includes(targetFormType)) {
    const labels = resolveOptionLabels(rawValue, sourceElement, getValueLabel, lng)
    if (labels.length === 0) {
      return rawValue
    }
    return labels.join(", ")
  }

  if (NUMBER_SET_FORM_TYPES.includes(targetFormType)) {
    const labels = resolveOptionLabels(rawValue, sourceElement, getValueLabel, lng)
    if (labels.length === 0 || !labels.every(isValidNumericLabel)) {
      return null
    }
    if (labels.length === 1) {
      return Number(String(labels[0]).trim())
    }
    return null
  }

  if (OPTION_SET_FORM_TYPES.includes(targetFormType)) {
    return matchTargetOptionValue(rawValue, sourceElement, targetElement, getValueLabel, lng)
  }

  return rawValue
}

export const dateDiff = (date1, date2) => {
  let formatedDate1 = safeDayjs(date1).format("YYYY-MM-DD")
  let formatedDate2 = safeDayjs(date2).format("YYYY-MM-DD")
  return safeDayjs(formatedDate1).diff(safeDayjs(formatedDate2))
}

// Rows inside one scenario are AND (the builder's default, including a missing
// operator). Scenarios on the same condition are OR: any one that passes is
// enough to run the actions.
export const isConditionOrOperator = (operator) => {
  const op = String(operator || "").trim().toLowerCase()
  return op === "||" || op === "or"
}

export const foldScenarioCondition = (finalResult, result, operator, isFirst) => {
  if (isFirst) return !!result
  if (isConditionOrOperator(operator)) return !!(finalResult || result)
  return !!(finalResult && result)
}

export const anyScenarioPassed = (passedScenarios = []) => passedScenarios.some(Boolean)

export const NUMBER_COMPARE_COMPARATORS = ["greaterThan", "greaterThanOrEqualTo", "lessThan", "lessThanOrEqualTo"]

export const resolveConditionFieldLookup = (reference, data) => {
  let lookupKey = reference
  let tableColumnValues = null
  let tableRefInfo = null
  if (lookupKey && String(lookupKey).includes("::")) {
    const [tableId, columnId] = lookupKey.split("::")
    const tableRows = data?.[tableId]
    const hasRowScopedValue = Object.prototype.hasOwnProperty.call(data || {}, columnId)
    if (hasRowScopedValue) {
      lookupKey = columnId
    } else if (Array.isArray(tableRows)) {
      tableRefInfo = { tableId, columnId, tableRows }
      tableColumnValues = tableRows
        .map((row) => row?.[columnId])
        .filter((value) => value !== undefined && value !== null && value !== "")
    }
  }
  if (data && !Object.prototype.hasOwnProperty.call(data, lookupKey) && String(lookupKey || "").includes("::")) {
    lookupKey = lookupKey.split("::")[1]
  }
  let currentValue = data?.[lookupKey]
  if ((currentValue === undefined || currentValue === null || currentValue === "") && data && lookupKey != null) {
    const wanted = String(lookupKey)
    const matchKey = Object.keys(data).find((key) => String(key) === wanted)
    if (matchKey) currentValue = data[matchKey]
  }
  if (Array.isArray(tableColumnValues) && tableColumnValues.length > 0) {
    currentValue = tableColumnValues
  }
  return { lookupKey, currentValue, tableColumnValues, tableRefInfo }
}

const userFormDoesNotContain = (currentValue, conditionValue) => {
  const elementValues = [
    ...(conditionValue?.user || []),
    ...(conditionValue?.group || []),
    ...(conditionValue?.role || []),
    ...(conditionValue?.userPicker || [])
  ]
  const currentValues = [
    ...(currentValue?.user || []),
    ...(currentValue?.group || []),
    ...(currentValue?.role || []),
    ...(currentValue?.userPicker || [])
  ]
  return !elementValues.some((val) => currentValues.includes(val))
}

const compareDateTime = (comparator, currentValue, elementValue, formType) => {
  if (elementValue === "$STATIC") return { skip: true }
  if (formType === "dateForm") {
    const formDate = safeDayjs(currentValue).format("YYYY-MM-DD")
    const compareValue = elementValue === "$TODAY" ? dayjs().format("YYYY-MM-DD") : elementValue
    if (!compareValue || !currentValue) return { result: false }
    if (comparator === "isBefore") return { result: safeDayjs(formDate).isBefore(safeDayjs(compareValue)) }
    if (comparator === "isAfter") return { result: safeDayjs(formDate).isAfter(safeDayjs(compareValue)) }
    const elementDate = safeDayjs(compareValue).format("YYYY-MM-DD")
    if (comparator === "beforeOrEqualTo") return { result: !!(elementDate && formDate <= elementDate) }
    if (comparator === "afterOrEqualTo") return { result: !!(elementDate && formDate >= elementDate) }
  }
  if (formType === "timeForm") {
    if (!currentValue) return { result: false }
    const formTime = safeDayjs(currentValue).format("HH:mm")
    const compareTime = safeDayjs(elementValue).format("HH:mm")
    if (comparator === "isBefore") return { result: formTime < compareTime }
    if (comparator === "isAfter") return { result: formTime > compareTime }
    if (comparator === "beforeOrEqualTo") return { result: formTime <= compareTime }
    if (comparator === "afterOrEqualTo") return { result: formTime >= compareTime }
  }
  return { result: false }
}

// Shared by form conditions and workflow "set approver" conditions. The two
// handlers only differ in what they do after a scenario passes.
export const evaluateFormFieldComparator = ({ element, data, formType, foundWidget, currentValue, lookupKey }) => {
  let result = false
  const fieldValue = lookupKey !== undefined ? data?.[lookupKey] : currentValue

  if (element.comparator === "isBetween") {
    if (formType === "dateForm" && data) {
      const range = element.value
      if (fieldValue && range && range.from && range.to) {
        if (dateDiff(fieldValue, range.from) >= 0 && dateDiff(fieldValue, range.to) <= 0) {
          result = true
        }
      }
    } else if (formType === "numberForm" && data) {
      const range = element.value
      if (fieldValue && range && range.from && range.to) {
        if ((fieldValue - range.from) >= 0 && (fieldValue - range.to) <= 0) {
          result = true
        }
      }
    }
  } else if (element.comparator === "isEqualTo" && data) {
    if (formType === "dateForm") {
      if (fieldValue && dateDiff(fieldValue, element.value) === 0) {
        result = true
      }
    } else {
      // Do not require formType. Public submit often cannot resolve the widget,
      // and the old type gate then skipped a real value match (empty approvers).
      result = valuesAreEqual(currentValue ?? fieldValue, element.value, foundWidget)
    }
  } else if (element.comparator === "isNotEqualTo" && data) {
    if (formType === "dateForm") {
      if (fieldValue && dateDiff(fieldValue, element.value) !== 0) {
        result = true
      }
    } else if (currentValue !== undefined && currentValue !== null && currentValue !== "") {
      result = !valuesAreEqual(currentValue ?? fieldValue, element.value, foundWidget)
    }
  } else if (element.comparator === "contains" && data) {
    if (formType === "inputForm" || formType === "dropdownField" || formType === "textAreaForm" || formType === "multipleChoiceField" || formType === "radioField" || formType === "contactField" || formType === "addressField") {
      if (Array.isArray(currentValue)) {
        if (currentValue.some((value) => value?.includes && value.toLowerCase().includes(element.value.toLowerCase()))) result = true
      } else if (currentValue && currentValue.toLowerCase().includes(element.value.toLowerCase())) {
        result = true
      }
    } else if (formType === "linkField") {
      const conditionValexists = currentValue?.find(val => val.label?.toLowerCase().includes(element.value.toLowerCase()))
      if (conditionValexists) {
        result = true
      }
    }
  } else if (element.comparator === "doesNotcontain" && data) {
    if (formType === "multipleChoiceField" || formType === "radioField") {
      const elementValues = parseConditionValueList(element.value)
      const selectedValues = Array.isArray(currentValue) ? currentValue : parseConditionValueList(currentValue)
      if (elementValues.length > 0 && selectedValues.length > 0) {
        result = !elementValues.some(value => selectedValues.some(selected => selected == value))
      }
    } else if (formType === "numberForm" || formType === "inputForm" || formType === "dropdownField" || formType === "textAreaForm" || formType === "linkForm" || formType === "addressField" || formType === "contactField") {
      if (Array.isArray(currentValue) && currentValue.some((value) => value?.includes && !value.toLowerCase().includes(element.value.toLowerCase()))) {
        result = true
      } else if (currentValue && !currentValue.toLowerCase().includes(element.value.toLowerCase())) {
        result = true
      }
    } else if (formType === "linkField") {
      const conditionValexists = currentValue?.find(val => !val.label?.toLowerCase().includes(element.value.toLowerCase()))
      if (conditionValexists) {
        result = true
      }
    } else if (formType === "userForm") {
      result = userFormDoesNotContain(fieldValue, element.value)
    }
  } else if (element.comparator === "containsAllOf" && data) {
    result = valuesContainAllOf(currentValue ?? fieldValue, element.value, foundWidget)
  } else if (element.comparator === "containsAnyOf" && data) {
    result = formType === "userForm"
      ? userFormContainsAnyOf(fieldValue, element.value)
      : valuesContainAnyOf(currentValue ?? fieldValue, element.value, foundWidget)
  } else if (element.comparator === "isEmpty" && data) {
    if (formType === "linkField") {
      if (currentValue?.length === 1 && !currentValue[0].label) {
        result = true
      }
    } else if (formType === "userForm") {
      if (currentValue && (currentValue?.user?.length === 0 && currentValue?.group?.length === 0)) {
        result = true
      }
    } else if (isValueEmpty(currentValue)) {
      result = true
    }
  } else if (element.comparator === "isNotEmpty" && data) {
    if (formType === "linkField") {
      if (currentValue?.length === 1 && currentValue[0].label) {
        result = true
      }
    } else if (formType === "userForm") {
      if (currentValue && (currentValue?.user?.length > 0 || currentValue?.group?.length > 0)) {
        result = true
      }
    } else if (!isValueEmpty(currentValue)) {
      result = true
    }
  } else if (element.comparator === "on" && data) {
    if (fieldValue === true) {
      result = true
    }
  } else if (element.comparator === "off" && data) {
    if (!fieldValue) {
      result = true
    }
  } else if (element.comparator === "lessThan") {
    result = compareNumbers(fieldValue, "lessThan", element.value)
  } else if (element.comparator === "lessThanOrEqualTo") {
    result = compareNumbers(fieldValue, "lessThanOrEqualTo", element.value)
  } else if (element.comparator === "greaterThan") {
    result = compareNumbers(fieldValue, "greaterThan", element.value)
  } else if (element.comparator === "greaterThanOrEqualTo") {
    result = compareNumbers(fieldValue, "greaterThanOrEqualTo", element.value)
  } else if (element.comparator === "isEven") {
    if (fieldValue && Number(fieldValue) % 2 === 0) {
      result = true
    }
  } else if (element.comparator === "isOdd") {
    if (fieldValue && Number(fieldValue) % 2 !== 0) {
      result = true
    }
  } else if (element.comparator === "charEquals") {
    if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).length === Number(element.value)) {
      result = true
    }
  } else if (element.comparator === "charLengthGreaterThanOrEqualTo") {
    if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).length >= Number(element.value)) {
      result = true
    }
  } else if (element.comparator === "charLengthLessThanOrEqualTo") {
    if (fieldValue !== undefined && fieldValue !== null && String(fieldValue).length <= Number(element.value)) {
      result = true
    }
  } else if (element.comparator === "isToday") {
    const today = dayjs().format("YYYY-MM-DD")
    const formDate = currentValue ? safeDayjs(currentValue).format("YYYY-MM-DD") : ""
    if (formDate && today === formDate) {
      result = true
    }
  } else if (["isBefore", "isAfter", "beforeOrEqualTo", "afterOrEqualTo"].includes(element.comparator)) {
    if (foundWidget) {
      return compareDateTime(element.comparator, currentValue, element.value, foundWidget.formType)
    }
  }

  return { result }
}

// "If Category Domain is empty, set Category Domain to 05" tests the very field it
// writes, so writing the value is what makes the condition fail. Taking the value
// back would make it pass again, and the two would trade places forever - such a
// set has to stay put once applied.
const readsOwnTarget = (actionBlock, targetId) => {
  if (!targetId) return false
  return !!actionBlock?.scenarios?.some((scenario) => scenario?.conditions?.some((condition) => {
    const reference = condition?.reference
    if (typeof reference !== "string") return false
    // A table column is referenced as `tableId::columnId`.
    return reference === targetId || reference.split("::")[1] === targetId
  }))
}

export const formConditionsHandler = (conditions, formData, elementData, elements = [], currentWorkflow = "", userInfo = {}, userGroups = [], scoreLookup = {}) => {
  let data = formData, modCondition = [];
  let passedAction = [];
  conditions.forEach(condition => {
    let actionExist = condition.actions.find(action => action.field === elementData.id)
    if (actionExist) {
      modCondition = modCondition.concat(condition)
    }
  })

  if (modCondition.length === 0) {
    return []
  }

  // Built lazily and once per call, instead of re-walking the whole element tree
  // inside the innermost condition-row loop below. First-wins on duplicate ids, to
  // match the .find() this replaces (sections are pushed alongside widgets, and
  // table columns get synthesised `tableId::colId` ids).
  let widgetById = null
  const findWidgetById = (widgetId) => {
    if (!widgetById) {
      widgetById = new Map()
      getAllWidgets(elements).forEach(widget => {
        if (widget && !widgetById.has(widget.id)) widgetById.set(widget.id, widget)
      })
    }
    return widgetById.get(widgetId)
  }

  for (let actionBlockIndex = 0; actionBlockIndex < modCondition.length; actionBlockIndex++) {
    const actionBlock = modCondition[actionBlockIndex];
    let passedScenario = []
    let actionBlockRowMatch = {}
    for (let blockIndex = 0; blockIndex < actionBlock?.scenarios.length; blockIndex++) {
      const conditionsBlock = actionBlock?.scenarios[blockIndex];
      let finalResult = false
      let hasFolded = false
      let operator = conditionsBlock.operator;
      let scenarioRowMatch = {}
      for (let index = 0; index < conditionsBlock.conditions.length; index++) {
        const element = conditionsBlock.conditions[index];
        let result = false
        let tableRefInfo = null
        if (element.parent === "Form field") {
          let foundWidget = findWidgetById(element.reference)
          if (!foundWidget) {
            // Number compares can use a previous form's stored value. Other
            // comparators still need the widget on this form — fail this row
            // rather than aborting the scenario as a success (finalResult used
            // to stay true when this `break` ran first).
            if (NUMBER_COMPARE_COMPARATORS.includes(element.comparator)) {
              foundWidget = { formType: "numberForm", multiSelect: null }
            }
          }
          const lookup = resolveConditionFieldLookup(element.reference, data)
          tableRefInfo = lookup.tableRefInfo
          const evaluation = evaluateFormFieldComparator({
            element,
            data,
            formType: foundWidget?.formType,
            foundWidget,
            currentValue: lookup.currentValue,
            lookupKey: lookup.lookupKey,
          })
          if (evaluation.skip) continue
          result = !!evaluation.result

        } else if (element.parent === "Workflow step") {
          if (element.comparator === "isEqualTo" && currentWorkflow === element.value) {
            result = true
          } else if (element.comparator === "isNotEqualTo" && currentWorkflow !== element.value) {
            result = true
          }
        } else if (element.parent === "User") {
          let currentValue = element.value || ""
          if (element.comparator === "isEqualTo") {
            if (currentValue && currentValue == userInfo.email) {
              result = true
            }
          } else if (element.comparator === "isNotEqualTo") {
            if (currentValue && currentValue != userInfo.email) {
              result = true
            }
          }
        } else if (element.parent == "Group") {
          let currentValue = element.value || ""
          if (currentValue && element.comparator === "isEqualTo") {
            let groupInUserGroups = userGroups?.find((group) => group.groupId === currentValue)
            if (groupInUserGroups) {
              result = true;
            }
          } else if (element.comparator === "isNotEqualTo") {
            let groupExist = false
            let groupInUserGroups = userGroups?.find((group) => group.groupId === currentValue)
            if (groupInUserGroups) {
              groupExist = true;
            }
            if (!groupExist) {
              result = true
            }
          }

        } else if (element.parent === "connectedUser") {
          let currentValue = element.value || {}
          if (element.comparator === "containedIn") {
            if (userInfo.isAdmin) {
              result = true
            }
            let pickerValues = { user: currentValue.user || [], group: currentValue.group || [] }
            if (currentValue?.userPicker?.length > 0) {
              currentValue.userPicker.map(picker => {
                let currentPickerValues = getUserPickervalues(picker, formData)
                if (currentPickerValues) {
                  if (currentPickerValues.user) {
                    pickerValues.user.push(...currentPickerValues.user)
                  }
                  if (currentPickerValues.group) {
                    pickerValues.group.push(...currentPickerValues.group)
                  }
                }
              })
            }
            if (pickerValues && pickerValues.user && pickerValues.user?.includes(userInfo.email)) {
              result = true
            }
            let userGroupsMapped = userGroups.map(group => group.groupId)
            let groupInUserGroups = pickerValues.group?.find(group => userGroupsMapped.includes(group))
            if (groupInUserGroups) {
              result = true
            }
          }
        } else if (element.parent === "Score") {
          let scoreEntry = (scoreLookup || {})[element.reference]
          let currentValue = scoreEntry ? scoreEntry.scoreObtained : undefined
          if (element.comparator === "isEqualTo") {
            if (currentValue !== undefined && currentValue !== null && currentValue == element.value) result = true
          } else if (element.comparator === "isNotEqualTo") {
            if (currentValue !== undefined && currentValue !== null && currentValue != element.value) result = true
          } else if (element.comparator === "greaterThan") {
            result = compareNumbers(currentValue, "greaterThan", element.value)
          } else if (element.comparator === "lessThan") {
            result = compareNumbers(currentValue, "lessThan", element.value)
          } else if (element.comparator === "greaterThanOrEqualTo") {
            result = compareNumbers(currentValue, "greaterThanOrEqualTo", element.value)
          } else if (element.comparator === "lessThanOrEqualTo") {
            result = compareNumbers(currentValue, "lessThanOrEqualTo", element.value)
          } else if (element.comparator === "isEmpty") {
            if (currentValue === undefined || currentValue === null) result = true
          } else if (element.comparator === "isNotEmpty") {
            if (currentValue !== undefined && currentValue !== null) result = true
          } else if (element.comparator === "isEven") {
            if (currentValue !== undefined && currentValue !== null && Number(currentValue) % 2 === 0) result = true
          } else if (element.comparator === "isOdd") {
            if (currentValue !== undefined && currentValue !== null && Math.abs(Number(currentValue) % 2) === 1) result = true
          }
        }

        if (result && tableRefInfo && Array.isArray(tableRefInfo.tableRows)) {
          const { tableId, columnId, tableRows } = tableRefInfo
          const matchedRowIndex = tableRows.findIndex((row) => {
            const rowValue = row?.[columnId]
            if (rowValue === undefined || rowValue === null || rowValue === "") {
              return false
            }
            if (element.comparator === "isEqualTo") {
              return rowValue == element.value
            }
            if (element.comparator === "contains") {
              return typeof rowValue === "string" && rowValue.includes(element.value)
            }
            if (element.comparator === "isNotEqualTo") {
              return rowValue != element.value
            }
            if (element.comparator === "doesNotcontain") {
              // A multiple choice column holds a list of option values, and the
              // condition can name several of them, so both are matched as lists
              if (isConditionValueList(element.value) || Array.isArray(rowValue)) {
                const conditionValues = parseConditionValueList(element.value)
                const rowValues = Array.isArray(rowValue) ? rowValue : [rowValue]
                return conditionValues.length > 0 && rowValues.length > 0 && !conditionValues.some((value) => rowValues.some((val) => val == value))
              }
              return typeof rowValue === "string" && !rowValue.includes(element.value)
            }
            return false
          })
          if (matchedRowIndex !== -1) {
            scenarioRowMatch[tableId] = matchedRowIndex
            actionBlockRowMatch[tableId] = matchedRowIndex
          }
        }

        // Handle direct Table Column references (when parent is not "Form field" but refers to a column ID directly)
        // This handles the case where the condition builder saves the column ID as the reference, 
        // but the parent might be ambiguous or the column is treated as a top-level field in some contexts.
        else {
          if (data && data.hasOwnProperty(element.reference)) {
            let currentValue = data[element.reference];
            let formType = "inputForm"; // Default to input comparison

            // Try to determine type from the element/widget definition if possible, otherwise generic
            // We can look up the column definition if we had access to the table structure here easily, 
            // but often we just rely on value comparison.

            if (element.comparator === "isEqualTo") {
              if (currentValue == element.value) result = true;
            } else if (element.comparator === "isNotEqualTo") {
              if (currentValue !== undefined && currentValue !== null && currentValue !== "" && currentValue != element.value) result = true;
            } else if (element.comparator === "contains") {
              if (currentValue && currentValue.includes && currentValue.includes(element.value)) result = true;
            } else if (element.comparator === "doesNotcontain") {
              // Same list handling as above: the column can hold several answers
              // and the condition can name several options
              if (isConditionValueList(element.value) || Array.isArray(currentValue)) {
                const conditionValues = parseConditionValueList(element.value)
                const currentValues = Array.isArray(currentValue) ? currentValue : parseConditionValueList(currentValue)
                if (conditionValues.length > 0 && currentValues.length > 0) {
                  result = !conditionValues.some((value) => currentValues.some((val) => val == value))
                }
              } else if (currentValue && currentValue.includes && !currentValue.includes(element.value)) {
                result = true;
              }
            } else if (element.comparator === "isEmpty") {
              if (isValueEmpty(currentValue)) result = true;
            } else if (element.comparator === "isNotEmpty") {
              if (!isValueEmpty(currentValue)) result = true;
            } else if (element.comparator === "greaterThan") {
              result = compareNumbers(currentValue, "greaterThan", element.value)
            } else if (element.comparator === "lessThan") {
              result = compareNumbers(currentValue, "lessThan", element.value)
            } else if (element.comparator === "greaterThanOrEqualTo") {
              result = compareNumbers(currentValue, "greaterThanOrEqualTo", element.value)
            } else if (element.comparator === "lessThanOrEqualTo") {
              result = compareNumbers(currentValue, "lessThanOrEqualTo", element.value)
            }
          }
        }

        finalResult = foldScenarioCondition(finalResult, result, operator, !hasFolded)
        hasFolded = true
        if (isConditionOrOperator(operator)) {
          if (finalResult) break
        } else if (!result) {
          break
        }
      }
      if (conditionsBlock.conditions.length == 0) {
        finalResult = false
      }
      passedScenario.push(finalResult)
    }
    // Scenarios are OR: one matching group is enough to run the shared actions.
    let currentScenarioPassed = anyScenarioPassed(passedScenario)
    if (currentScenarioPassed) {
      let reqAction = actionBlock.actions?.filter(action => action.field === elementData.id)

      // Also include actions that have no specific field or apply to the element in a special way
      // For setCellColor, the field IS the elementData.id (the table), so it should have been caught.
      // But let's double check if there are other criteria or if action.field is missing.

      if (reqAction) {
        const hasRowMatch = Object.keys(actionBlockRowMatch).length > 0
        const contextualAction = reqAction.map((action) => {
          if (!hasRowMatch) {
            return action
          }
          return { ...action, matchedRows: { ...actionBlockRowMatch } }
        })
        passedAction = passedAction.concat(contextualAction)
      }
    } else {
      // A half-built condition (a "set" action saved before any scenario condition
      // was configured) always evaluates as failed. Don't let it clear the target
      // field's value - it was never really applied in the first place.
      const hasConfiguredConditions = actionBlock?.scenarios?.some((scenario) => scenario?.conditions?.length > 0)
      if (hasConfiguredConditions) {
        // Only this element's own actions, exactly as the passing branch above does -
        // a condition that also writes into other fields must not leave placeholders
        // on this one.
        let reqAction = actionBlock.actions?.filter(action => action.field === elementData.id && action.action === "set")
          .map(action => ({ ...action, value: null, __inactiveSet: true, __selfReferential: readsOwnTarget(actionBlock, elementData.id) })) || []
        passedAction = passedAction.concat(reqAction)
      }
    }
  }
  return passedAction

}

const splitElementValue = (element) => {
  let elementValue = (element.value && element.value.value) ? element.value.value : ""
  if (Array.isArray(elementValue)) {
    elementValue = elementValue[0] || ""
  }
  return elementValue ? elementValue.split(",") : ""
}

export const getCurrentWorkflowForms = (forms, nodes, nextStepName, requestWorkflow, defaultCycle = 1, currentCycle = 1) => {

  let currentForms = requestWorkflow.filter(step => step.formData?.formId || step.formId)
  currentForms = currentForms.map(step => {
    let workflowForm = forms?.find(form => form.formId === step.formId)
    return { ...workflowForm }
  })
  return currentForms
}

export const findWidgets = (list, sectionLabel = "", options = {}) => {
  const { includeSection } = options
  let mod = []
  if (!list) {
    return mod
  }
  list.forEach(listItem => {
    if (listItem.formType === "sectionForm") {
      if (includeSection) {
        mod.push({ ...listItem })
        mod.push(...findWidgets(listItem.children, listItem.label, { includeSection: includeSection }))
      } else {
        mod.push(...findWidgets(listItem.children || [], listItem.label, { includeSection: includeSection }))
      }
    } else {
      let reqObj = { ...listItem }

      if (sectionLabel) {
        reqObj.sectionLabel = sectionLabel
      }
      mod.push({ ...reqObj })
    }
  })
  return mod
}

export const getDueDate = (stepData, _requestDetail) => {
  if (!stepData) return ""
  const nested = stepData.data
  const data = nested && typeof nested === "object" &&
    (nested.deadline != null || nested.count != null || nested.period != null)
    ? nested
    : stepData
  if (!data?.deadline) return ""
  if (data.count == null || data.count === "") return ""
  const reqDate = moment().add(data.count, data.period || "day")
  return reqDate.isValid() ? reqDate.format("YYYY-MM-DD") : ""
}

export const approvalDelayed = (dueDate) => {
  if (!dueDate) {
    return false
  }
  let curDate = new Date(dayjs().format("YYYY/MM/DD"));
  let date2 = dueDate ? new Date(dueDate) : curDate;
  let diff = curDate - date2;
  if (diff > 0) {
    return true
  } else {
    return false
  }
}

const isTodayBetween = (startDate, endDate) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  return today >= start && today <= end;
};

export const getDelegatedUser = (userInfo, approvers, spaceSpecificUsers) => {
  let delegatedUsers = []
  let approverUsers = approvers?.user || []
  if (approverUsers?.length > 0) {

    approverUsers.map(user => {
      let userInSpace = spaceSpecificUsers.find(spaceUser => spaceUser.email === user)
      if (userInSpace?.delegatedUser && userInSpace.delegationStartDate && userInSpace.delegationDueDate) {
        let validDelegationDate = isTodayBetween(userInfo.delegationStartDate, userInfo.delegationDueDate)
        if (validDelegationDate) {
          delegatedUsers.push(userInSpace?.delegatedUser)
        }
      }
    })
  }

  return delegatedUsers
  // let assignees = assignedTo.user.concat(assignedTo.group);
  // let delegatedUser = approvers.filter(approver => !assignees.includes(approver))

  // return delegatedUser.length > 0 ? delegatedUser[0] : ""

}

// Re-key every id-bearing part of a single element: option values, rating scale
// questions, and table columns (including each column's own option values).
// Options fed from master data (parentTable) keep their values, since those are
// the master row uuids the mapping relies on.
const rekeyElementKeys = (element) => {
  const reqElement = { ...element }

  if (!reqElement.parentTable && Array.isArray(reqElement.options) && reqElement.options.length > 0) {
    reqElement.options = reqElement.options.map(option => ({ ...option, value: uuidv4() }))
  }

  if (Array.isArray(reqElement.questions) && reqElement.questions.length > 0) {
    reqElement.questions = reqElement.questions.map(question => ({ ...question, value: uuidv4() }))
  }

  if (Array.isArray(reqElement.columns) && reqElement.columns.length > 0) {
    reqElement.columns = reqElement.columns.map(column => {
      const reqColumn = { ...column, id: uuidv4() }
      if (!reqColumn.parentTable && Array.isArray(reqColumn.options) && reqColumn.options.length > 0) {
        reqColumn.options = reqColumn.options.map(option => ({ ...option, value: uuidv4() }))
      }
      return reqColumn
    })
  }

  return reqElement
}

// Deep copy of any form element: the element, every descendant (nested sections
// included) and every nested key gets a fresh id so no two elements in the form
// ever share one.
// Drop translations on the element and every descendant: copies get new ids
// (and re-keyed option values / column ids), so the stored translated text
// (and id/value-keyed list translations) would be stale. Copies start
// untranslated and fall back to their new defaults until re-translated.
export const copyFormElement = (element) => {
  const { translations: _translations, ...rest } = element || {}
  const reqElement = rekeyElementKeys(rest)
  reqElement.id = uuidv4()

  if (Array.isArray(element?.children) && element.children.length > 0) {
    reqElement.children = element.children.map(child => copyFormElement(child))
  }

  return reqElement
}

export const findParentObjectById = (items, id, parent) => {
  let found = null
  for (const obj of items) {
    if (obj.id === id && !parent) {
      return null
    } else if (obj.id === id && parent) {
      return parent
    } else if (obj.children && obj.children.length > 0) {
      found = findParentObjectById(obj.children, id, obj);
      if (found) return found; // Only return if we actually found the parent
    }
  }
  return found;
}

export const setFormItemDefaultAttribute = (element) => {
  let reqElement = { ...element };
  const defaultRatingScaleQuestions = [
    "The overall quality of the products",
    "The quality of packaging",
    "The quality of store's presentation",
    "The quality of customer service",
  ];
  const defaultRatingScaleOptions = ["Very Poor", "Poor", "Neutral", "Good", "Excellent"];
  const ensureRatingScaleItems = (items, count, defaults, prefix, withScore = false) => {
    const safeCount = Math.min(Math.max(Number(count) || defaults.length, 1), 10);
    return Array.from({ length: safeCount }, (_, index) => {
      const existing = items?.[index] || {};
      return {
        ...existing,
        label: existing.label || defaults[index] || `${prefix} ${index + 1}`,
        value: existing.value || uuidv4(),
        ...(withScore ? { score: existing.score ?? index + 1 } : {}),
      };
    });
  };
  switch (reqElement.formType) {
    case "section":
      if (reqElement.collapsible === undefined) {
        reqElement.collapsible = true
      }
      if (reqElement.defaultCollapse === undefined) {
        reqElement.defaultCollapse = false
      }
      break;

    case "divider":
      if (reqElement.textAlignment === undefined) {
        reqElement.textAlignment = "center"
      }
      break
    case 'radioForm':
      if (!reqElement.sort) {
        reqElement.sort = "ascending"
      }
      if (!reqElement.layout) {
        reqElement.layout = false
      }
      break;

    case 'multipleChoiceField':
      if (!reqElement.sort) {
        // reqElement.sort = "ascending"
      }
      break;

    case 'dropdownField':
      if (!reqElement.sort) {
        // reqElement.sort = "ascending"
      }
      // Only master data backed dropdowns expose the autopopulate toggle.
      // Carry a pre-rename `autoPopulate` value onto the key the switch now binds
      // to, so an existing form shows its real state instead of reading as off.
      if ((reqElement.parentTable || reqElement.isMasterData) && reqElement.autoPopulateMaster === undefined) {
        reqElement.autoPopulateMaster = reqElement.autoPopulate ?? false
      }
      break;

    case "imageForm":
      if (reqElement.textAlignment === undefined) {
        reqElement.textAlignment = "left"
      }
      break

    case "timeForm":
      if (!reqElement.timeFormat) {
        reqElement.timeFormat = "HH:mm"
      }
      if (!reqElement.placeholder) {
        reqElement.placeholder = ""
      }
      break;

    case "dateForm":
      if (!reqElement.dateFormat) {
        reqElement.dateFormat = "DD-MM-YYYY"
      }
      if (!reqElement.placeholder) {
        reqElement.placeholder = ""
      }
      if (reqElement.relativeDateEnabled === undefined) {
        reqElement.relativeDateEnabled = false
      }
      if (!reqElement.relativeDateDirection) {
        reqElement.relativeDateDirection = "after"
      }
      if (!reqElement.relativeDateCountSource) {
        reqElement.relativeDateCountSource = "static"
      }
      if (!reqElement.relativeDateUnit) {
        reqElement.relativeDateUnit = "day"
      }
      break;

    case "tableForm":
      if (!reqElement.rowCount) {
        reqElement.rowCount = 2
      }
      if (!reqElement.columns) {
        reqElement.columns = []
      }
      if (!reqElement.tooltip) {
        reqElement.tooltip = false
      }
      if (!reqElement.mandatory) {
        reqElement.mandatory = false
      }
      if (!reqElement.readOnly) {
        reqElement.readOnly = false
      }
      if (!reqElement.hideInitially) {
        reqElement.hideInitially = false
      }
      // Tables prefilled with default rows still accept new rows unless the
      // author turns this off.
      if (reqElement.allowNewRow === undefined) {
        reqElement.allowNewRow = true
      }
      break;
    case "ratingScaleField":
      if (!reqElement.label) {
        reqElement.label = "Please rate our store based on your recent experience of shopping with us?"
      }
      reqElement.questionCount = Math.min(Math.max(Number(reqElement.questionCount) || defaultRatingScaleQuestions.length, 1), 10)
      reqElement.optionCount = Math.min(Math.max(Number(reqElement.optionCount) || defaultRatingScaleOptions.length, 1), 10)
      reqElement.questions = ensureRatingScaleItems(reqElement.questions, reqElement.questionCount, defaultRatingScaleQuestions, "Question")
      reqElement.options = ensureRatingScaleItems(reqElement.options, reqElement.optionCount, defaultRatingScaleOptions, "Option", true)
      if (reqElement.tooltip === undefined) {
        reqElement.tooltip = false
      }
      if (reqElement.mandatory === undefined) {
        reqElement.mandatory = false
      }
      if (reqElement.readOnly === undefined) {
        reqElement.readOnly = false
      }
      if (reqElement.hideInitially === undefined) {
        reqElement.hideInitially = false
      }
      break;
    default:
  }
  return reqElement
}

export const convertKeyToLabel = (masterData) => {
  let reqData = masterData.map(data => {
    let reqObj = {}
    Object.keys(data).forEach(key => {
      let reqKey = getLabel(key)
      reqObj[reqKey] = data[key]
    })
    return { ...reqObj }
  })
  return reqData
}

export function findDuplicates(items) {
  const duplicates = [];

  const checkDuplicates = (elements) => {
    const labelCount = elements.reduce((acc, el) => {
      if (el.formType === "infoForm") {
        return acc
      }
      acc[el.label] = (acc[el.label] || 0) + 1;
      return acc;
    }, {});
    return elements.filter(el => labelCount[el.label] > 1);
  };

  const traverse = (elements) => {
    const levelDuplicates = checkDuplicates(elements);
    if (levelDuplicates.length > 0) {
      duplicates.push(...levelDuplicates);
    }
    for (const element of elements) {
      if (element.children && element.children.length > 0) {
        traverse(element.children);
      }
    }
  };

  traverse(items);
  return duplicates;
}

export const findMatch = (data, key, value, parentKey, rec) => {
  let result = []
  data.map(r => {
    if (r[key] == value) {
      result.push({ ...r, parentKey: parentKey, parentValue: rec?.value })
    }
    if (r.children?.length) {
      result.push(...findMatch(r.children, key, value, r.label, r))
    }
  })
  return result
}

export const getActiveSetValue = (setValueData, filterNull = false) => {
  let activeSetValue = null;
  if (setValueData && setValueData.length > 0) {
    // Several conditions can target the same field, and each one contributes an entry
    // here in the order the conditions are listed - a condition that is not satisfied
    // right now keeps its slot as an __inactiveSet placeholder. So the first entry is
    // not necessarily the one that writes a value: take the first that actually does,
    // otherwise an unsatisfied first condition would mask the value a later one sets.
    activeSetValue = setValueData.find((s) => s && !s.__inactiveSet && s.value !== undefined && s.value !== null) || null
    if (!activeSetValue) {
      // Nothing supplies a value any more. Keep the failed "set" action (marked
      // __inactiveSet with a null value) so a read-only field can clear the value
      // the condition wrote earlier instead of holding on to a stale one.
      const inactive = setValueData.find((s) => s?.__inactiveSet) || null
      activeSetValue = filterNull ? inactive : (inactive || setValueData[0] || null)
    }
  }
  return activeSetValue
}

// Widgets write dates as dayjs, multiple choice as arrays and user pickers as
// objects, so identity alone cannot tell whether two form values are the same
// selection.
const isSameConditionalValue = (a, b) => {
  if (a === b) return true
  if (a === undefined || a === null || b === undefined || b === null) return false
  if (dayjs.isDayjs(a) || dayjs.isDayjs(b)) {
    return dayjs.isDayjs(a) && dayjs.isDayjs(b) ? a.isSame(b) : false
  }
  if (typeof a === "object" || typeof b === "object") {
    try {
      return JSON.stringify(a) === JSON.stringify(b)
    } catch {
      return false
    }
  }
  return String(a) === String(b)
}

// A "set" action becomes an __inactiveSet placeholder as soon as its scenarios stop
// matching - say the currency the condition tested on gets changed. The value it
// wrote has to be taken back with it, or the field keeps showing a result the
// current form data no longer supports.
//
// The field gives back just what the condition wrote, which is what `appliedValue`
// (the widget's record of its own last write) is compared against - a value the
// user picked afterwards is theirs and stays. Widgets that pass `disabled` clear a
// read-only field outright as well, on the grounds that only a condition can have
// filled it. When that write is taken back, `getConditionalRevertValue` puts the
// field's saved default back if it has one, instead of leaving the field empty.
export const shouldRevertConditionalValue = (activeSetValue, { disabled = false, currentValue, appliedValue } = {}) => {
  if (!activeSetValue || activeSetValue.value) return false
  if (disabled) return true
  if (!activeSetValue.__inactiveSet || activeSetValue.__selfReferential) return false
  if (appliedValue === undefined) return false
  return isSameConditionalValue(currentValue, appliedValue)
}

// Value to write when a "set" condition stops matching. Prefer the field's saved
// default (the one authored on the form) so a failed condition does not wipe a
// fallback the form still expects; otherwise use `emptyValue` (usually null).
export const getConditionalRevertValue = (fieldId, defaultFormValues, emptyValue = null) => {
  if (!fieldId || !defaultFormValues || typeof defaultFormValues !== "object") {
    return emptyValue
  }
  if (!Object.prototype.hasOwnProperty.call(defaultFormValues, fieldId)) {
    return emptyValue
  }
  const fallback = defaultFormValues[fieldId]
  if (fallback === undefined || fallback === null || fallback === "") {
    return emptyValue
  }
  return fallback
}

export const findWidgetByWidgetId = (array, targetUUID, parent) => {
  for (const obj of array) {
    if (obj.widgetId === targetUUID) {
      return {
        result: obj,
        parent: parent
      }
    } else if (obj.children && obj.children.length > 0) {
      const result = findWidgetByWidgetId(obj.children, targetUUID, obj);
      if (result) {
        return result;
      }
    }
  }
  return null;
}

export const getCurrentStepValues = (currentValues, formValues) => {
  let curValues = {};
  for (const key in currentValues) {
    if (!formValues.hasOwnProperty(key) && currentValues[key]) {
      curValues[key] = currentValues[key]
    }
  }
  return curValues
}

// ...existing code...
export const getPublicFormItems = (formItems) => {
  if (!Array.isArray(formItems)) return [];
  const filterRecursive = (items) => {
    return items.reduce((acc, item) => {
      if (!item) return acc;
      // skip items that are explicitly requestor-only
      if (item.isRequestor === true) return acc;
      const cloned = { ...item };
      if (Array.isArray(cloned.children)) {
        cloned.children = filterRecursive(cloned.children);
      }
      acc.push(cloned);
      return acc;
    }, []);
  };
  return filterRecursive(formItems);
}

export const getRequestorFields = (formItems) => {
  if (!Array.isArray(formItems)) return [];
  const results = [];
  const traverse = (items, ancestorIsRequestor = false) => {
    items.forEach(item => {
      if (!item) return;
      const isReq = ancestorIsRequestor || item.isRequestor === true;
      if (isReq) {
        // push a shallow clone to avoid mutating original objects
        results.push({ ...item });
      }
      if (Array.isArray(item.children) && item.children.length > 0) {
        traverse(item.children, isReq);
      }
    });
  };
  traverse(formItems, false);
  return results;
}

export const formatPostValue = (value = {}) => {
  let formattedValue = {};

  Object.keys(value).map(key => {
    if (value[key]?.file || value[key]?.fileList) {
      let fileValue = value[key].file || {}, fileLists = value[key].fileList;
      formattedValue[key] = {
        file: { lastModifiedDate: fileValue?.lastModifiedDate || null, uid: fileValue.uid, name: fileValue.name, type: fileValue.type, originFileObj: fileValue.originFileObj },
        fileList: fileLists.map(file => ({ lastModifiedDate: file.lastModifiedDate || null, uid: file.uid, name: file.name, type: file.type, originFileObj: fileValue.originFileObj }))
      }
    } else if (Array.isArray(value[key])) {
      formattedValue[key] = value[key].filter((item) => item !== null && item !== undefined)
    } else {
      formattedValue[key] = value[key]
    }
  })
  return formattedValue
}

export const getFormDefaultValues = (defaultValues = {}) => {
  let formDefaultValues = { ...defaultValues }
  Object.keys(formDefaultValues).forEach(key => {
    let formValue = formDefaultValues[key]
    if (!formValue) {
      delete formDefaultValues[key]
    } else if (typeof formValue !== "string" && !Array.isArray(formValue) && (formValue.hasOwnProperty("user") || formValue.hasOwnProperty("group") || formValue.hasOwnProperty("userPicker"))) {
      let pickerValueCount = 0, pickerTypes = Object.keys(formValue)
      for (let i = 0; i < pickerTypes.length; i++) {
        pickerValueCount += formValue[pickerTypes[i]]?.length
        if (pickerValueCount > 0) {
          break
        }
      }
      if (pickerValueCount === 0) {
        delete formDefaultValues[key]
      }
    } else if (Array.isArray(formValue)) {

      let valArr = []
      for (let i = 0; i < formValue.length; i++) {
        let curValue = formValue[i];
        if (typeof curValue === "string" && curValue) {
          let formattedString = curValue.replaceAll(" ", "")
          if (formattedString.length > 0) {
            valArr.push(curValue)
          }
        } else if (typeof curValue === "object" && curValue !== null) {
          // Check if object has any non-empty values
          let hasValidValue = false
          for (const objKey in curValue) {
            if (curValue[objKey] && (typeof curValue[objKey] !== "string" || curValue[objKey].toString().trim().length > 0)) {
              hasValidValue = true
              break
            }
          }
          if (hasValidValue) {
            valArr.push(curValue)
          }
        } else if (curValue) {
          valArr.push(curValue)
        }
      }
      if (valArr.length === 0) {
        delete formDefaultValues[key]
      }
    }
  })
  return { ...formDefaultValues }
}

export const getSpaceSpecificUsers = (spaceSpecificUsers, predefinedUserList = null, selectedOptions, getSpaceUsers = false, predefinedGroupList = []) => {
  let users = predefinedUserList || [];
  if (getSpaceUsers && (!predefinedUserList || predefinedUserList?.length === 0) && (!predefinedGroupList || predefinedGroupList?.length === 0)) {
    users = [...spaceSpecificUsers]
  } else if (predefinedUserList && predefinedUserList.length > 0) {
    users = [...predefinedUserList]
  } else {
    return []
  }

  users = users.map(item => {
    let reqItem = item
    if (reqItem && typeof reqItem === "string") {
      reqItem = spaceSpecificUsers.find(user => user.email === item) || { email: item }
    }
    let fullName = reqItem?.name || ""
    return { ...reqItem, name: fullName }
  })
  let listOfUsers = [];
  users.forEach(user => {
    if (selectedOptions.user?.includes(user?.email)) {
      listOfUsers.unshift(user);
    } else {
      listOfUsers.push(user);
    }
  })

  return listOfUsers;
}

export const getSpaceSpecificGroups = (spaceSpecificGroups, predefinedGroupList = null, selectedOptions, getSpaceUsers = false, predefinedUserList = []) => {
  let groups = predefinedGroupList || [];
  if (getSpaceUsers && ((!predefinedGroupList || predefinedGroupList?.length === 0) && (!predefinedUserList || predefinedUserList?.length === 0))) {
    groups = [...spaceSpecificGroups]
  } else if (predefinedGroupList && predefinedGroupList.length > 0) {
    groups = predefinedGroupList.map(item => {
      let reqItem = item
      if (typeof reqItem === "string") {
        reqItem = spaceSpecificGroups.find(group => group.groupId === item)
      }
      return { ...reqItem }
    })
  } else {
    return []
  }

  let listOfGroups = [];
  groups.forEach(group => {
    if (selectedOptions.group?.includes(group?.email)) {
      listOfGroups.unshift(group);
    } else {
      listOfGroups.push(group);
    }
  })

  return listOfGroups;

}

export const getFormattedFormItems = (items) => {
  let formItems = items?.map(item => {
    if (item.isTableColumnComputation) {
      return { ...item, tableId: item.calculationField?.tableId, columnId: item.calculationField?.columnId }
    } else if (item.children) {
      let sectionItems = item.children.map(child => {
        if (child.isTableColumnComputation) {
          return { ...child, tableId: child.calculationField?.tableId, columnId: child.calculationField?.columnId }
        }
        return { ...child }
      })
      item.children = sectionItems
    }
    return { ...item }
  })
  return formItems;
}

// A "set" action counts as active only when it actually supplies a value right now:
// a half-built condition is marked __inactiveSet, and a dynamic action carries the id
// of the field it copies from rather than the value itself, so that field has to hold
// something for the action to write anything. `$TODAY` is resolved by the widget.
const isActiveSetAction = (action, formValues = {}) => {
  if (!action || action.action !== "set" || action.__inactiveSet) {
    return false
  }
  if (action.valueSource === "dynamic") {
    if (!action.dynamicField) return false
    if (action.dynamicField === "$TODAY") return true
    const dynamicValue = formValues?.[action.dynamicField]
    return dynamicValue !== undefined && dynamicValue !== null && dynamicValue !== ""
  }
  return action.value !== undefined && action.value !== null && action.value !== ""
}

export const hasActiveSetAction = (actions = [], formValues = {}) =>
  Array.isArray(actions) && actions.some((action) => isActiveSetAction(action, formValues))

// Whether a condition writes a value into this element or, for a container, into
// anything below it. A hidden element that answers yes still owns a value, so it is
// kept alive (off screen) and collected into the submitted values.
export const hasConditionalSetValue = (element, conditions = [], formValues = {}, elements = [], currentStatus = "", userInfo = {}, userGroups = []) => {
  if (!element || !conditions || conditions.length === 0) {
    return false
  }
  const actions = formConditionsHandler(conditions, formValues, element, elements, currentStatus, userInfo, userGroups)
  if (hasActiveSetAction(actions, formValues)) {
    return true
  }
  const children = element.children || []
  return children.some((child) => hasConditionalSetValue(child, conditions, formValues, elements, currentStatus, userInfo, userGroups))
}
